import React from 'react';
import { useTranslation } from 'react-i18next';

interface NewAnalysisConfirmModalProps {
    isOpen: boolean;
    onConfirm: (shouldSave: boolean) => void;
    onCancel: () => void;
    variant?: 'default' | 'leavePage';
}

/**
 * Modal de confirmation avant de démarrer une nouvelle analyse
 * Proposé quand une analyse non sauvegardée est en cours
 */
export const NewAnalysisConfirmModal: React.FC<NewAnalysisConfirmModalProps> = ({
    isOpen,
    onConfirm,
    onCancel,
    variant = 'default',
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const message = variant === 'leavePage'
        ? (t('features.swimlane.unsavedAnalysisLeaveMessage') ||
            'Vous avez une analyse en cours non sauvegardée. Voulez-vous la sauvegarder avant de quitter cette page ?')
        : (t('features.swimlane.unsavedAnalysisMessage') ||
            'Vous avez une analyse en cours non sauvegardée.');

    const continueMessage = variant === 'leavePage'
        ? null
        : (t('features.swimlane.continueWithoutSaving') ||
            'Voulez-vous continuer sans sauvegarder ?');

    const ignoreLabel = variant === 'leavePage'
        ? (t('features.swimlane.leaveWithoutSaving') || 'Quitter sans sauvegarder')
        : (t('features.swimlane.ignoreAndContinue') || 'Ignorer et continuer');

    return (
        <div 
            className="modal fade show d-block" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 9999 }}
            tabIndex={-1}
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered">
                <div
                    className="modal-content analysis-save-modal border-0 shadow-lg"
                    style={{ borderRadius: '12px', backgroundColor: '#fff' }}
                >
                    {/* Header */}
                    <div className="modal-header border-0 pb-2">
                        <h5 className="modal-title d-flex align-items-center text-gray-800">
                            <i className="fa-solid fa-triangle-exclamation me-2" style={{ color: '#FFA500' }}></i>
                            <span>{t('features.swimlane.unsavedAnalysis') || 'Analyse non sauvegardée'}</span>
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onCancel}
                            aria-label="Close"
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body pt-0">
                        <p className={`text-gray-700 ${continueMessage ? 'mb-3' : 'mb-0'}`} style={{ fontSize: '0.95rem' }}>
                            {message}
                        </p>

                        {continueMessage && (
                            <p className="mb-0 text-gray-700" style={{ fontSize: '0.95rem' }}>
                                {continueMessage}
                            </p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-0 pt-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onCancel}
                            style={{ borderRadius: '8px' }}
                        >
                            <i className="fa-solid fa-arrow-left me-2"></i>
                            {t('words.back') || 'Retour'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => onConfirm(true)}
                            style={{ borderRadius: '8px' }}
                        >
                            <i className="fa-solid fa-save me-2"></i>
                            {t('words.save') || 'Sauvegarder'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => onConfirm(false)}
                            style={{ borderRadius: '8px' }}
                        >
                            <i className="fa-solid fa-xmark me-2"></i>
                            {ignoreLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

